import React, {useState} from 'react';
import clsx from 'clsx';
import {KeenIcon} from '@/components';
import HoverPopup from "@/components/hover-popup/HoverPopup.tsx";
import {Characteristic, Importance, SkillLevel} from "@/pages/buddy/types/Characteristic";
import {Skill} from "@/pages/buddy/types/MatchDetailsData.ts";
import axios from "axios";
import {enqueueSnackbar} from "notistack";

export const importanceColorMapping: Record<Importance, string> = {
    Essential: "bg-[#F39200] text-white",
    Desirable: "bg-[#F7A94B] text-white",
    Optional: "bg-[#FBC276] text-white",
    Unspecified: "bg-gray-200",
};

export const levelStarsMapping: Record<SkillLevel, number> = {
    Novice: 1,
    Amateur: 2,
    Intermediate: 3,
    Professional: 4,
    Expert: 5,
    Unspecified: 0,
};

interface StarIndicatorProps {
    level: SkillLevel;
    isReliable?: boolean;
}

const StarIndicator = ({level, isReliable}: StarIndicatorProps) => {
    // Otherwise, display stars corresponding to the level.
    const filledStars = levelStarsMapping[level];
    return (
        <div className="flex ml-2 mr-0">
            {Array.from({length: filledStars}).map((_, i) => (
                <KeenIcon
                    key={i}
                    icon="abstract-23"
                    style="solid"
                    className={i === filledStars - 1 ? "w-2.5 h-3" : "w-3.5 h-3"}
                />
            ))}
        </div>
    );
};

interface CharacteristicBadgeProps {
    // always required
    entity: 'job' | 'candidate' | 'match';
    characteristicJob?: Characteristic;
    characteristicCandidate?: Characteristic;
    skill?: Skill;

    // only used when entity === 'match'
    matchId?: string;
    criterion?: string;             // dash-case soft-criterion
    onRefresh?: () => Promise<void>;
}

const CharacteristicBadge: React.FC<CharacteristicBadgeProps> = ({
                                                                     characteristicCandidate,
                                                                     characteristicJob,
                                                                     entity,
                                                                     skill,
                                                                     matchId,
                                                                     criterion,
                                                                     onRefresh,
                                                                 }) => {
    const normalizedLevelJob: SkillLevel = characteristicJob?.level || "Unspecified";
    const normalizedLevelCandidate: SkillLevel = characteristicCandidate?.level || "Unspecified";

    const abs = skill?.soft_score_absolute ?? 0;
    const max = skill?.soft_score_absolute_max ?? 0;
    const scoreDisplay = abs > 0 ? `${abs} / ${max}` : `${max}`;
    const scoreText =
        abs > 0
            ? `This skill scores ${abs} out of ${max} possible points.`
            : `Maximal achievable points: ${max}`;

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(abs > 0 ? abs : max);
    const [loading, setLoading] = useState(false);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setEditValue(abs > 0 ? abs : max);
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        if (entity !== 'match' || !skill) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const url = `${import.meta.env.VITE_APP_API_URL}/match/${matchId}/matched-skill/${criterion}`;

            // build the single‐element array request payload
            const updatedSkill: Skill = {
                ...skill,
                soft_score_absolute: editValue,
            };

            await axios.put(
                url,
                {matched_skills: [updatedSkill]},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // refresh if provided
            await onRefresh?.();
            enqueueSnackbar("Score updated", { variant: "solid", state: "success" });
            setIsEditing(false);
        } catch (err: any) {
            console.error('Failed to save score', err);
            enqueueSnackbar(`Failed to save score: ${err.message}`, {
                variant: 'solid',
                state: 'danger',
            });
        } finally {
            setLoading(false);
        }
    };

    const popupContent = (
        <div className="text-xs space-y-1">
            {(entity === "job" || entity === "match") && characteristicJob && (
                <div>Importance: {characteristicJob.importance}</div>
            )}

            {(entity === "job" || entity === "match") && characteristicJob && (
                <div className="flex items-center">
                    <span>Expected Skill Level: {normalizedLevelJob}</span>
                    <StarIndicator level={normalizedLevelJob} isReliable={skill?.is_reliable ?? false}/>
                </div>
            )}

            {entity === "candidate" && characteristicCandidate && (
                <div className="flex items-center">
                    <span>Candidate Skill Level: {normalizedLevelCandidate}</span>
                    <StarIndicator level={normalizedLevelCandidate} isReliable={skill?.is_reliable ?? false}/>
                </div>
            )}

            {entity === "match" && characteristicCandidate && (
                <div className="flex items-center">
                    <span>Candidate Skill Level: {normalizedLevelCandidate}</span>
                    <StarIndicator level={normalizedLevelCandidate} isReliable={skill?.is_reliable ?? false}/>
                </div>
            )}

            {(entity !== "candidate" && entity !== "job") || (entity === "job" && max > 0) ? (
                <div>{scoreText}</div>
            ) : null}

            {(
                (entity === "job" && normalizedLevelJob === "Unspecified") ||
                (entity === "candidate" && normalizedLevelCandidate === "Unspecified") ||
                (entity !== "job" && entity !== "candidate" && !skill?.is_reliable)
            ) && (
                <div className="text-xs text-red-500 mt-1 flex items-center">
                    <KeenIcon icon="information-3" className="w-3.5 h-3 mr-1"/>
                    This skill may be unreliable because the {entity === "job"
                    ? "expected level wasn’t specified"
                    : entity === "candidate"
                        ? "candidate skill level wasn’t specified"
                        : "skill information is incomplete"}.
                </div>
            )}
        </div>
    );

    // popup footer (match only)
    const footer = entity === 'match' && (
        <div className="text-xs w-full">
            {!isEditing ? (
                <div className="flex justify-end">
                    <button
                        onClick={handleEditClick}
                        className="btn btn-light btn-sm flex items-center"
                    >
                        <KeenIcon icon="notepad-edit"/>
                        Edit Score
                    </button>
                </div>
            ) : (
                <>
                    <div className="flex justify-end space-x-2 mb-2">
                        <button
                            onClick={handleSaveClick}
                            className="btn btn-primary btn-sm flex items-center"
                            disabled={loading}
                        >
                            <KeenIcon icon="file-up"/>
                            {loading ? 'Saving...' : 'Save Score'}
                        </button>
                        <button
                            onClick={handleCancelClick}
                            className="btn btn-secondary btn-sm flex items-center"
                            disabled={loading}
                        >
                            <KeenIcon icon="tablet-delete"/>
                            Cancel
                        </button>
                    </div>
                    <div className="mt-4 flex items-center space-x-2">
                        <input
                            type="range"
                            min={0}
                            max={max}
                            value={editValue}
                            onChange={e => setEditValue(Number(e.target.value))}
                            className="custom-range flex-1"
                        />
                        <span className="text-xs">{editValue} / {max}</span>
                    </div>
                </>
            )}
        </div>
    );

    const name = entity === "candidate"
        ? characteristicCandidate?.name
        : characteristicJob?.name;

    const importance = characteristicJob?.importance ?? "Unspecified";

    return (
        <HoverPopup
            popupHeader={
                <h3 className="card-title text-base font-semibold">
                    {name || "Unnamed Characteristic"}
                </h3>
            }
            popupContent={popupContent}
            popupFooter={footer}
            delay={500}
            popupWidthClass="min-w-[300px] max-w-[450px]"
        >
            <span
                className={clsx(
                    "badge badge-sm",
                    importanceColorMapping[importance]
                )}
            >
                {(entity === "job" && normalizedLevelJob === "Unspecified") ||
                (entity === "candidate" && normalizedLevelCandidate === "Unspecified") ||
                (entity !== "job" && entity !== "candidate" && !skill?.is_reliable) ? (
                    <KeenIcon icon="information-3" className="w-3.5 h-3 mr-1"/>
                ) : null}

                {name || "Unnamed Characteristic"}

                {entity === "job" && normalizedLevelJob !== "Unspecified" && (
                    <StarIndicator
                        level={normalizedLevelJob}
                        isReliable={skill?.is_reliable ?? false}
                    />
                )}

                {entity === "candidate" && normalizedLevelCandidate !== "Unspecified" && (
                    <StarIndicator
                        level={normalizedLevelCandidate}
                        isReliable={skill?.is_reliable ?? false}
                    />
                )}

                {skill && (
                    <span className="ml-2 px-1 rounded bg-gray-200 text-gray-700 leading-none text-[10px] "
                          style={{paddingTop: "1px", paddingBottom: "1px"}}>
                        {scoreDisplay}
                    </span>
                )}
            </span>
        </HoverPopup>
    );
};

export default CharacteristicBadge;
